import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { WizardStepProps } from '../types';
import { BROKER_PROVIDERS, searchBrokers, BrokerProvider } from '../../../../data/brokers';
import { Theme } from '@mui/material/styles';

// Flat UI Color System - Theme Aware (matching INSIGHT_DESIGN_PRINCIPLES.md)
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    surfaceHover: isDark ? "#2a2a2a" : "#f1f5f9", // Hover state backgrounds
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray
    borderSelected: "#22c55e", // Green border for selected state

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text
  };
};

// Flat UI Typography System (matching INSIGHT_DESIGN_PRINCIPLES.md)
const flatUITypography = {
  // Display - Main headings
  display: {
    fontSize: "2rem", // 32px
    fontWeight: 700, // Bold
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },

  // Body - General content
  body: {
    fontSize: "1rem", // 16px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },

  // Caption - Small text
  caption: {
    fontSize: "0.875rem", // 14px
    fontWeight: 400,
    lineHeight: 1.4,
  },
};

const BrokerSelectionStep: React.FC<WizardStepProps> = ({ onNext, onCancel }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBroker, setSelectedBroker] = useState<BrokerProvider | null>(null);

  // Filter brokers based on search
  const filteredBrokers = useMemo(() => {
    const brokers = searchQuery ? searchBrokers(searchQuery) : BROKER_PROVIDERS;
    return brokers.filter(b => b.status === 'available' || b.status === 'beta');
  }, [searchQuery]);

  const handleSelectBroker = (broker: BrokerProvider) => {
    setSelectedBroker(broker);
  };

  const handleContinue = () => {
    if (selectedBroker) {
      onNext({ broker: selectedBroker });
    }
  };

  return (
    <>
      {/* Header - Flat UI Typography */}
      <Box sx={{ mb: 4 }}>
        <Typography
          component="h1"
          sx={{
            ...flatUITypography.display,
            color: flatUIColors.textPrimary,
            mb: 1,
          }}
        >
          Select Your Broker
        </Typography>
        <Typography 
          sx={{
            ...flatUITypography.body,
            color: flatUIColors.textSecondary,
          }}
        >
          Choose the investment platform you'd like to connect
        </Typography>
      </Box>

      {/* Search bar - Clean Flat Design */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search brokers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: flatUIColors.surface,
              border: `1px solid ${flatUIColors.border}`,
              borderRadius: 1, // Sharp corners
              '&:hover': {
                backgroundColor: flatUIColors.surfaceHover,
              },
              '&.Mui-focused': {
                borderColor: flatUIColors.primary,
              },
              '& fieldset': {
                border: 'none',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: flatUIColors.textSecondary }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Broker grid with flex-grow for scrollable area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 3, pr: 1 }}>
        <Stack
          spacing={2}
          sx={{
            '& > *': {
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
              minWidth: { xs: '100%', sm: '280px' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 8px)' },
            },
            '@media (min-width: 600px)': {
              flexDirection: 'row',
              flexWrap: 'wrap',
            },
          }}
        >
          {filteredBrokers.map((broker) => (
            <Box key={broker.id}>
                <Card
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    backgroundColor: selectedBroker?.id === broker.id
                      ? flatUIColors.surface
                      : flatUIColors.background,
                    border: `2px solid ${
                      selectedBroker?.id === broker.id
                        ? flatUIColors.borderSelected
                        : flatUIColors.border
                    }`,
                    borderRadius: 1, // Sharp corners
                    boxShadow: 'none', // Remove all shadows
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedBroker?.id === broker.id
                        ? flatUIColors.surface
                        : flatUIColors.surfaceHover,
                      borderColor: selectedBroker?.id === broker.id
                        ? flatUIColors.borderSelected
                        : flatUIColors.textSecondary,
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleSelectBroker(broker)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent', // Remove default hover background
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* Direct logo rendering without box wrapper */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 72, // Larger size for better visibility
                            height: 72,
                          }}
                        >
                          <broker.iconComponent size={48} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography 
                              sx={{
                                ...flatUITypography.body,
                                fontWeight: 600,
                                color: flatUIColors.textPrimary,
                                fontSize: '1.1rem',
                              }}
                            >
                              {broker.displayName}
                            </Typography>
                            {broker.status === 'beta' && (
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.25,
                                  backgroundColor: flatUIColors.surface,
                                  border: `1px solid ${flatUIColors.border}`,
                                  borderRadius: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    ...flatUITypography.caption,
                                    color: flatUIColors.textSecondary,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  Beta
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography 
                            sx={{
                              ...flatUITypography.caption,
                              color: flatUIColors.textSecondary,
                              lineHeight: 1.5,
                            }}
                          >
                            {broker.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
            </Box>
          ))}
        </Stack>

        {filteredBrokers.length === 0 && (
          <Fade in>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No brokers found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Footer actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button
          variant="text"
          onClick={onCancel}
          sx={{ 
            color: flatUIColors.textSecondary,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: flatUIColors.surface,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedBroker}
          endIcon={<ArrowForwardIcon />}
          sx={{
            minWidth: 120,
            backgroundColor: flatUIColors.primary,
            color: 'white',
            borderRadius: 1, // Sharp corners
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none', // Remove shadow
            '&:hover': {
              backgroundColor: flatUIColors.primaryHover,
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: flatUIColors.border,
              color: flatUIColors.textMuted,
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </>
  );
};

export default BrokerSelectionStep;